import * as React from "react";
import { useEffect, useState } from "react";
import {
  Badge,
  Heading,
  Search,
  Spinner,
  Tabs,
} from "@digdir/designsystemet-react";
import AthleteCard from "./Components/AthleteCard";
import NewsArticleCard from "./Components/NewsArticleCard";
import VideoCard from "./Components/VideoCard";
import EventCard from "./Components/EventCard";
import GeneralCard from "./Components/GeneralCard";
import {
  SearchPageViewModel,
  SearchResultItem,
  SearchResultItemType,
  Translations,
} from "./types";

const facetKeys = [
  "all",
  "video",
  "athletes",
  "news",
  "events",
  "other",
] as const;
type FacetKey = (typeof facetKeys)[number];
// map each facet key to the exact SearchResultItemType you want
type PageFacetKey = Exclude<FacetKey, "all" | "video">;
const facetTypeMap: Record<PageFacetKey, SearchResultItemType> = {
  athletes: SearchResultItemType.Athlete,
  news: SearchResultItemType.NewsArticle,
  events: SearchResultItemType.Event,
  other: SearchResultItemType.General,
};

const emptyResults = {
  allPages: [],
  allVideos: [],
  allCount: 0,
  noSearchResult: true,
};

type SearchResultProps = { item: SearchResultItem };
// --- Sub‐components for rendering one search result or one video ---
const SearchResult: React.FC<SearchResultProps> = ({ item }) => {
  switch (item.searchResultType) {
    case SearchResultItemType.Athlete:
      return item.athleteData ? <AthleteCard {...item.athleteData} /> : null;

    case SearchResultItemType.NewsArticle:
      return item.newsArticleData ? (
        <NewsArticleCard {...item.newsArticleData} />
      ) : null;

    case SearchResultItemType.Event:
      return item.eventData ? <EventCard {...item.eventData} /> : null;

    case SearchResultItemType.General:
    default:
      return item.generalData ? <GeneralCard {...item.generalData} /> : null;
  }
};

// --- Main SearchPage component ---
const SearchPage = ({
  model,
  translations,
}: {
  model: SearchPageViewModel;
  translations: Translations;
}) => {
  const {
    initialSearchModel: {
      searchTerm: initTerm,
      searchRootPageId,
      boostFrontPageId,
    },
    searchResultModel: initResults,
  } = model;

  const t = translations;
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState(initTerm || "");
  const [hasSearched, setHasSearched] = useState(false);
  const [results, setResults] = useState(initResults);
  const [activeFacet, setActiveFacet] = useState("all");

  // helper: fire search API
  const doSearch = async (term: any) => {
    setLoading(true);
    try {
      const res = await fetch("/api/searchapi/search", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          searchTerm: term,
          searchRootPageId,
          boostFrontPageId,
        }),
      });
      if (!res.ok) throw new Error(`API failed: ${res.status}`);
      const data = await res.json();
      setResults(data);
      setHasSearched(true);
      setActiveFacet("all");
    } catch (err) {
      console.error(err);
      // optionally display an error here
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const term = searchTerm.trim();
    const p = new URLSearchParams(window.location.search);
    term ? p.set("q", term) : p.delete("q");
    window.history.pushState({}, "", `${location.pathname}?${p.toString()}`);
    term ? doSearch(term) : (setResults(emptyResults), setHasSearched(false));
  };

  const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setSearchTerm("");
    const p = new URLSearchParams(window.location.search);
    p.delete("q");
    window.history.replaceState(
      {},
      "",
      `${location.pathname}${p.toString() ? `?${p}` : ""}`
    );
    setResults(emptyResults);
    setHasSearched(false);
  };

  const handleSuggestion = (suggestion: string) => {
    setSearchTerm(suggestion);
    doSearch(suggestion);
  };

  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get("q")?.trim();
    if (q) {
      setSearchTerm(q);
      doSearch(q);
    } else {
      setResults(emptyResults);
      setHasSearched(false);
    }
  }, []);

  // pre‐compute counts for each facet
  const pageItems = results.allPages || [];

  const filteredPages = () => {
    if (activeFacet === "all") return pageItems;
    if (activeFacet === "video") return [];
    // tell TS: at this point activeFacet must be a PageFacetKey
    const key = activeFacet as PageFacetKey;
    return pageItems.filter((i) => i.searchResultType === facetTypeMap[key]);
  };

  const counts: Record<FacetKey, number> = {
    all: results.allCount, // total pages
    video: (results.allVideos || []).length, // total videos
    athletes: pageItems.filter(
      (i) => i.searchResultType === SearchResultItemType.Athlete
    ).length,
    news: pageItems.filter(
      (i) => i.searchResultType === SearchResultItemType.NewsArticle
    ).length,
    events: pageItems.filter(
      (i) => i.searchResultType === SearchResultItemType.Event
    ).length,
    other: pageItems.filter(
      (i) => i.searchResultType === SearchResultItemType.General
    ).length,
  };

  // --- Tab panel headings --- //
  const hits = filteredPages();
  const hasHits =
    activeFacet === "video"
      ? (results.allVideos?.length ?? 0) > 0
      : hits.length > 0;

  const facetLabel = t.facets[activeFacet as FacetKey];
  // "Treff i {facet}"
  const hasHitsLabel = `${t.ui.hits} ${
    t.ui.hitsIn
  } ${facetLabel.toLowerCase()}`;

  // "Ingen treff i {facet}"
  const noHitsLabel = `${t.ui.noResults} ${
    t.ui.hitsIn
  } ${facetLabel.toLowerCase()}`;

  const isAllOrOtherTab = activeFacet === "all" || activeFacet === "other";

  let tabHeading: string;
  if (hasHits) {
    tabHeading = isAllOrOtherTab
      ? `${facetLabel} ${t.ui.hits.toLowerCase()}` // "Alle/Andre treff"
      : hasHitsLabel;
  } else {
    tabHeading = noHitsLabel;
  }

  const showVideoHits =
    (activeFacet === "all" || activeFacet === "video") &&
    results.allVideos.length > 0;

  return (
    <div className="searchPage content">
      {/* Search form */}
      <form onSubmit={handleSubmit}>
        <Search>
          <Search.Input
            id="q"
            name="q"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t.ui.placeholder}
            aria-label={t.ui.placeholder}
          />
          <Search.Clear onClick={handleClear} />
          <Search.Button disabled={loading} />
        </Search>
      </form>

      {/* Tabs for facets + search results */}
      {hasSearched && !loading && !results.noSearchResult && (
        <Tabs
          value={activeFacet}
          onChange={(v) => setActiveFacet(v as FacetKey)}
        >
          {/* Gradient at the end of the tablist, to indicate horizontal scroll on smaller screens */}
          <div className="absolute right-0 h-10 w-12 z-10 bg-gradient-to-r from-transparent to-white pointer-events-none lg:h-12" />
          <Tabs.List>
            {facetKeys.map((key) => (
              <Tabs.Tab key={key} value={key}>
                {t.facets[key]}
                <Badge count={counts[key]}></Badge>
              </Tabs.Tab>
            ))}
          </Tabs.List>

          {/* Search results */}
          <Heading level={1}>{tabHeading}</Heading>
          <Tabs.Panel value={activeFacet}>
            {hits &&
              hits.map((item, i) => (
                <div key={i} className="cardWrapper">
                  <SearchResult item={item} />
                </div>
              ))}

            {/* Video results */}
            {showVideoHits && (
              <>
                {results.allVideos.map((video, idx) => (
                  <div key={idx} className="cardWrapper">
                    <VideoCard {...video} />
                  </div>
                ))}
              </>
            )}
          </Tabs.Panel>
        </Tabs>
      )}

      {/* Loading */}
      {hasSearched && loading && (
        <div className="statusWrapper">
          <div className="mx-auto">
            <Spinner aria-label={t.ui.loading} role="status" data-size="xl" />
          </div>
        </div>
      )}

      {/* No results */}
      {hasSearched &&
        results.noSearchResult &&
        !loading &&
        searchTerm != "" && (
          <div className="statusWrapper">
            <Heading level={1} className="heading-lg">
              {t.ui.noResults}
            </Heading>
          </div>
        )}
    </div>
  );
};

export default SearchPage;
